import React, { useState, useEffect, useRef } from 'react';
import { XIcon, ChevronDownIcon } from 'lucide-react';
import './EditStudentModal.css';

const EditStudentModal = ({ isOpen, onClose, student, onSave, courseOptions = [] }) => {
    /* --- Estados e Refs --- */
    const [formData, setFormData] = useState({ name: '', email: '', telefone: '', curso: '' });
    const [isCourseDropdownOpen, setIsCourseDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    /* --- Efeitos --- */
    // Popula o formulário quando um novo aluno é selecionado
    useEffect(() => {
        if (student) {
            setFormData({
                name: student.name || '',
                email: student.email || '',
                telefone: student.telefone || '',
                curso: student.curso || ''
            });
        }
    }, [student]);

    // Fecha o dropdown de cursos ao clicar fora dele
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsCourseDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    /* --- Manipuladores (Handlers) --- */
    // Atualiza o estado para campos de texto
    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    // Atualiza o estado para o dropdown de cursos
    const handleCourseSelect = (courseName) => {
        setFormData(prev => ({ ...prev, curso: courseName }));
        setIsCourseDropdownOpen(false);
    };

    // Salva as alterações e fecha o modal
    const handleSave = () => {
        onSave({ ...student, ...formData });
        onClose();
    };

    // Cláusula de guarda para não renderizar o modal se não for necessário
    if (!isOpen || !student) {
        return null;
    }

    /* --- Renderização --- */
    return (
        <div className="esm-modal-overlay">
            <div role="dialog" className="esm-modal-content">

                <button onClick={onClose} className="esm-close-button">
                    <XIcon className="esm-close-icon" />
                    <span className="esm-sr-only">Fechar</span>
                </button>

                <div className="esm-header">
                    <h2 className="esm-title">Editar Aluno</h2>
                </div>
                <p className="esm-description">Edite as informações do aluno</p>

                <div className="esm-body">
                    <div className="esm-form-group">
                        <label htmlFor="name" className="esm-label">Nome Completo</label>
                        <input id="name" className="esm-input" value={formData.name} onChange={handleChange} />
                    </div>
                    <div className="esm-form-group">
                        <label htmlFor="email" className="esm-label">Email</label>
                        <input id="email" type="email" className="esm-input" value={formData.email} onChange={handleChange} />
                    </div>
                    <div className="esm-form-group">
                        <label htmlFor="telefone" className="esm-label">Telefone</label>
                        <input id="telefone" className="esm-input" value={formData.telefone} onChange={handleChange} placeholder="Nenhum telefone cadastrado" />
                    </div>

                    <div className="esm-form-group">
                        <label htmlFor="curso" className="esm-label">Curso</label>
                        <div className="esm-select-container" ref={dropdownRef}>
                            <button
                                type="button"
                                className="esm-select-trigger"
                                onClick={() => setIsCourseDropdownOpen(!isCourseDropdownOpen)}
                                aria-haspopup="listbox"
                                aria-expanded={isCourseDropdownOpen}
                            >
                                <span className="esm-select-value">
                                    {formData.curso || "Selecione um curso"}
                                </span>
                                <ChevronDownIcon className="esm-select-icon" />
                            </button>

                            {isCourseDropdownOpen && (
                                <ul className="esm-select-options">
                                    {courseOptions.map((courseName) => (
                                        <li
                                            key={courseName}
                                            className="esm-select-option"
                                            onClick={() => handleCourseSelect(courseName)}
                                            role="option"
                                            aria-selected={formData.curso === courseName}
                                        >
                                            {courseName}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>

                <div className="esm-footer">
                    <button onClick={onClose} className="esm-button esm-button-secondary">
                        Cancelar
                    </button>
                    <button onClick={handleSave} className="esm-button esm-button-primary">
                        Salvar Alterações
                    </button>
                </div>

            </div>
        </div>
    );
};

export default EditStudentModal;